
import React, { useState, useEffect } from 'react';
import { User, UserProfileType } from '../types';
import { PlusCircle, Crown, Settings, Zap, Book, ExternalLink, Quote, Sparkles, Brain, Wand2, Clock, GraduationCap, ChevronRight, Users } from 'lucide-react';

interface Props {
  user: User;
  onNew: () => void;
  onAdmin?: () => void;
  onLibrary: () => void;
  onHistory: () => void;
  onPatients: () => void;
}

const Dashboard: React.FC<Props> = ({ user, onNew, onAdmin, onLibrary, onHistory, onPatients }) => {
  const [quote, setQuote] = useState("");

  const quotes = [
    "A mente é a sua verdadeira ferramenta de trabalho.",
    "Instrumentos físicos são apenas indutores da sua mentalização.",
    "Você consegue! A capacidade de projetar está em todos nós.",
    "Pondere sua vida digital: descanse a mente para ter foco.",
    "Na releitura, descobrimos o que ignoramos na primeira vez.",
    "Invista seu tempo ocioso em coisas que trazem crescimento espiritual.",
    "Não se engesse em regras; use sua criatividade e intuição.",
    "Sua vontade determina o que será feito e como será feito.",
    "Projete a cor com foco e precisão cronometrada.",
    "Comece agora! A prática do bem é o nosso grande objetivo."
  ];

  useEffect(() => {
    setQuote(quotes[Math.floor(Math.random() * quotes.length)]);
  }, []);
  
  const getPlanStyle = (p: UserProfileType) => {
    switch (p) {
      case UserProfileType.OURO: return { color: 'text-yellow-500 dark:text-yellow-400', label: 'Ouro Vitalício', bg: 'bg-yellow-500/10 border-yellow-500/30' };
      case UserProfileType.MASTER: return { color: 'text-red-600 dark:text-red-500', label: 'Master Admin', bg: 'bg-red-500/10 border-red-500/30' };
      case UserProfileType.PRATA: return { color: 'text-slate-600 dark:text-slate-300', label: 'Prata', bg: 'bg-slate-400/10 border-slate-400/30' };
      case UserProfileType.BRONZE: return { color: 'text-orange-600 dark:text-orange-400', label: 'Bronze', bg: 'bg-orange-500/10 border-orange-500/30' };
      default: return { color: 'text-gray-500 dark:text-gray-400', label: 'Gratuito', bg: 'bg-gray-400/10 border-gray-400/30' };
    }
  };

  const planStyle = getPlanStyle(user.profile);
  const hasPatientAccess = [UserProfileType.PRATA, UserProfileType.OURO, UserProfileType.MASTER].includes(user.profile);

  return (
    <div className="space-y-12 animate-fade-in">
      {/* Welcome Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
        <div>
          <div className="flex items-center gap-3 text-chroma-accent text-[10px] font-bold uppercase tracking-[0.4em] mb-4">
            <div className="w-8 h-1 bg-chroma-accent/40 rounded-full"></div>
            Campo de Atuação
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-chroma-text mb-2 tracking-tight">Mestre {user.name}</h2>
          <p className="text-chroma-muted text-lg font-light tracking-wide">Prática e Experiência • {new Date().toLocaleDateString('pt-BR')}</p>
        </div>
        <div className="flex gap-4 flex-wrap w-full md:w-auto">
          {hasPatientAccess && (
            <button onClick={onPatients} className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-purple-100 dark:bg-purple-900/30 text-chroma-text border border-purple-200 dark:border-white/10 px-6 py-4 rounded-2xl font-bold transition-all shadow-sm text-sm">
              <Users size={20} className="text-chroma-accent" />
              <span>Pacientes</span>
            </button>
          )}
          <button onClick={onHistory} className="flex-1 md:flex-none flex items-center justify-center gap-3 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 text-chroma-text border border-purple-200 dark:border-white/10 px-6 py-4 rounded-2xl font-medium transition-all shadow-sm text-sm">
            <Clock size={20} className="text-chroma-accent" />
            <span>Histórico</span>
          </button>
          <button onClick={onNew} className="w-full md:w-auto group flex items-center justify-center gap-3 bg-gradient-to-r from-chroma-accent to-purple-600 text-white px-8 py-4 rounded-2xl font-bold transition-all shadow-xl text-base">
            <PlusCircle size={24} />
            Novo Protocolo
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className={`p-10 rounded-[2.5rem] border shadow-lg ${planStyle.bg} flex flex-col justify-between transition-transform hover:scale-[1.01]`}>
          <div className="flex items-start justify-between mb-8">
            <Crown className={planStyle.color} size={40} />
            <span className={`text-[10px] font-bold px-4 py-1.5 rounded-full bg-white/50 dark:bg-black/40 border border-purple-200 uppercase tracking-[0.2em] ${planStyle.color}`}>
              {planStyle.label}
            </span>
          </div>
          <p className="text-chroma-text text-2xl font-light tracking-wide">
            {user.subscriptionExpiry ? `Acesso válido até ${new Date(user.subscriptionExpiry).toLocaleDateString()}` : 'Portal Vitalício • Sem expiração'}
          </p>
        </div>

        <div className="bg-chroma-card p-10 rounded-[2.5rem] border border-chroma-border shadow-lg flex flex-col justify-between transition-transform hover:scale-[1.01]">
          <div className="flex items-start justify-between mb-8">
            <Zap className="text-emerald-500" size={40} />
            <span className="text-[10px] font-bold px-4 py-1.5 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 uppercase tracking-[0.2em] border border-emerald-500/20">Projeções Hoje</span>
          </div>
          <p className="text-6xl font-bold text-chroma-text tracking-tighter">{user.requestsToday}</p>
        </div>
      </div>

      {/* Promocional: Curso Completo */}
      <div className="bg-gradient-to-br from-indigo-900/80 to-purple-900/80 border border-purple-400/30 p-10 md:p-14 rounded-[3rem] relative overflow-hidden shadow-2xl group">
         <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
            <GraduationCap size={200} />
         </div>
         <div className="relative z-10 flex flex-col md:flex-row items-center gap-10">
            <div className="shrink-0 p-6 bg-white/10 rounded-[2rem] backdrop-blur-xl">
               <Sparkles size={64} className="text-yellow-400 animate-pulse" />
            </div>
            <div className="flex-1 space-y-6 text-center md:text-left">
               <h3 className="text-3xl md:text-4xl font-bold text-white tracking-tight">Formação Técnica Master</h3>
               <p className="text-xl text-purple-100/80 font-light leading-relaxed tracking-wide">
                  Este aplicativo é apenas uma pequena amostra. Domine toda a metodologia João Caputo e torne-se um terapeuta de referência.
               </p>
               <a 
                 href="https://caputoacademia.com.br/venda_cromoterapia" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="inline-flex items-center gap-4 bg-gradient-to-r from-yellow-500 to-orange-600 text-white px-10 py-5 rounded-2xl font-black text-lg shadow-xl shadow-orange-900/40 hover:scale-105 transition-all uppercase tracking-widest"
               >
                  Quero o Curso Completo
                  <ChevronRight size={24} />
               </a>
            </div>
         </div>
      </div>

      {/* Pílula de Sabedoria */}
      <div className="bg-chroma-card border border-chroma-border p-10 rounded-3xl relative overflow-hidden group shadow-lg">
         <div className="absolute top-0 right-0 p-6 opacity-5 group-hover:rotate-12 transition-transform">
            <Brain size={120} />
         </div>
         <div className="flex items-start gap-8 relative z-10">
            <Quote className="text-chroma-accent shrink-0 opacity-50" size={40} />
            <div>
               <p className="text-xl md:text-2xl font-light text-chroma-text italic leading-relaxed mb-6 tracking-wide">"{quote}"</p>
               <p className="text-[10px] uppercase font-bold text-chroma-muted tracking-[0.5em] opacity-80">— João Caputo</p>
            </div>
         </div>
      </div>
    </div>
  );
};

export default Dashboard;
